import React, { useState, useEffect } from 'react';
import { apiPost } from '../utils/api';
import { Shield, Mail, User, Building, FileText, Copy, Check, X, Hash, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocation, Link as RouterLink, Routes, Route, useNavigate } from 'react-router-dom';

// Função utilitária para obter categoria e nome da ferramenta ativa
export function getActiveToolInfo(location) {
  const tabs = [
    { id: 'password', name: 'Validador de Senha', path: '/security/password' },
    { id: 'email', name: 'Validador de Email', path: '/security/email' },
    { id: 'cpf', name: 'Validador de CPF', path: '/security/cpf' },
    { id: 'cnpj', name: 'Validador de CNPJ', path: '/security/cnpj' },
    { id: 'hash', name: 'Hash Seguro', path: '/security/hash' },
    { id: 'verify', name: 'Verificar Hash', path: '/security/verify' }
  ];
  const match = tabs.find(tab => location.pathname.startsWith(tab.path));
  return { category: 'Segurança', tool: match ? match.name : null };
}

const Security = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Definição das abas e rotas
  const tabs = [
    { id: 'password', name: 'Validador de Senha', icon: Shield, path: '/security/password', description: 'Análise de força', status: 'active' },
    { id: 'email', name: 'Validador de Email', icon: Mail, path: '/security/email', description: 'Verificação de formato', status: 'active' },
    { id: 'cpf', name: 'Validador de CPF', icon: User, path: '/security/cpf', description: 'Validação completa', status: 'active' },
    { id: 'cnpj', name: 'Validador de CNPJ', icon: Building, path: '/security/cnpj', description: 'Validação completa', status: 'active' },
    { id: 'hash', name: 'Hash Seguro', icon: Hash, path: '/security/hash', description: 'Geração de hash', status: 'active' },
    { id: 'verify', name: 'Verificar Hash', icon: Search, path: '/security/verify', description: 'Verificação de hash', status: 'active' }
  ];

  // Redirecionar /security para /security/password
  useEffect(() => {
    if (location.pathname === '/security' || location.pathname === '/security/') {
      navigate('/security/password', { replace: true });
    }
  }, [location.pathname, navigate]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto w-full overflow-x-hidden bg-white min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Ferramentas de Segurança
        </h1>
        <p className="text-gray-600">
          Validadores de senha, email, CPF, CNPJ e formatadores
        </p>
      </div>

      {/* Navegação rápida das ferramentas */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Ferramentas Disponíveis</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tabs.filter(tab => tab.status === 'active').map(tab => (
            <RouterLink
              key={tab.id}
              to={tab.path}
              className={`p-4 bg-white border rounded-lg transition-all duration-200 flex items-center space-x-3 ${location.pathname === tab.path ? 'border-blue-500 bg-blue-50 shadow' : 'border-gray-200 hover:shadow-md hover:border-blue-300'}`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <tab.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-base md:text-[15px] lg:text-base leading-tight break-words whitespace-normal">{tab.name}</h3>
                <p className="text-sm text-gray-500 leading-snug break-words whitespace-normal">{tab.description}</p>
              </div>
            </RouterLink>
          ))}
        </div>
      </div>

      <Routes>
        <Route path="password" element={<PasswordValidator />} />
        <Route path="email" element={<EmailValidator />} />
        <Route path="cpf" element={<CPFValidator />} />
        <Route path="cnpj" element={<CNPJValidator />} />
        <Route path="hash" element={<HashGenerator />} />
        <Route path="verify" element={<HashVerifier />} />
      </Routes>
    </div>
  );
};

// Componente Validador de Senha
const PasswordValidator = () => {
  const [password, setPassword] = useState('');

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };

    const score = Object.values(checks).filter(Boolean).length;
    let strength = '';
    let color = '';

    if (score <= 2) {
      strength = 'Fraca';
      color = 'text-red-500';
    } else if (score <= 3) {
      strength = 'Média';
      color = 'text-yellow-500';
    } else if (score <= 4) {
      strength = 'Forte';
      color = 'text-blue-500';
    } else {
      strength = 'Muito Forte';
      color = 'text-green-500';
    }

    return { checks, score, strength, color };
  };

  const passwordValidation = validatePassword(password);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-md mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Senha
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Digite sua senha..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
          />
        </div>

        {password && (
          <div className="space-y-4">
            <div className="text-center">
              <span className={`text-lg font-semibold ${passwordValidation.color}`}>
                {passwordValidation.strength}
              </span>
            </div>

            <div className="space-y-2">
              {Object.entries(passwordValidation.checks).map(([check, isValid]) => (
                <div key={check} className="flex items-center space-x-2">
                  {isValid ? (
                    <Check className="text-green-500" size={16} />
                  ) : (
                    <X className="text-red-500" size={16} />
                  )}
                  <span className="text-sm text-gray-900 capitalize">
                    {check === 'length' && 'Pelo menos 8 caracteres'}
                    {check === 'uppercase' && 'Pelo menos uma maiúscula'}
                    {check === 'lowercase' && 'Pelo menos uma minúscula'}
                    {check === 'numbers' && 'Pelo menos um número'}
                    {check === 'symbols' && 'Pelo menos um símbolo'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Validador de Email
const EmailValidator = () => {
  const [email, setEmail] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    
    if (!email) return { isValid: false, message: 'Digite um email' };
    if (!isValid) return { isValid: false, message: 'Email inválido' };
    
    return { isValid: true, message: 'Email válido' };
  };

  const emailValidation = validateEmail(email);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-md mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Digite o email..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
          />
        </div>

        {email && (
          <div className={`text-center p-4 rounded-lg ${
            emailValidation.isValid 
              ? 'bg-green-50 text-green-700' 
              : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-center justify-center space-x-2">
              {emailValidation.isValid ? (
                <Check className="text-green-500" size={20} />
              ) : (
                <X className="text-red-500" size={20} />
              )}
              <span className="font-medium">{emailValidation.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Validador de CPF
const CPFValidator = () => {
  const [cpf, setCpf] = useState('');

  const validateCPF = (cpf) => {
    const clean = cpf.replace(/\D/g, '');
    if (clean.length !== 11) return { isValid: false, message: 'CPF deve ter 11 dígitos' };
    
    // Verificar se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(clean)) return { isValid: false, message: 'CPF inválido' };
    
    // Validar dígitos verificadores
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(clean[i]) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(clean[9])) return { isValid: false, message: 'CPF inválido' };
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(clean[i]) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(clean[10])) return { isValid: false, message: 'CPF inválido' };
    
    return { isValid: true, message: 'CPF válido' };
  };

  const formatCPF = (value) => {
    const clean = value.replace(/\D/g, '');
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const cpfValidation = validateCPF(cpf);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-md mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            CPF
          </label>
          <input
            type="text"
            value={cpf}
            onChange={(e) => setCpf(formatCPF(e.target.value))}
            placeholder="000.000.000-00"
            maxLength={14}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
          />
        </div>

        {cpf && (
          <div className={`text-center p-4 rounded-lg ${
            cpfValidation.isValid 
              ? 'bg-green-50 text-green-700' 
              : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-center justify-center space-x-2">
              {cpfValidation.isValid ? (
                <Check className="text-green-500" size={20} />
              ) : (
                <X className="text-red-500" size={20} />
              )}
              <span className="font-medium">{cpfValidation.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Validador de CNPJ
const CNPJValidator = () => {
  const [cnpj, setCnpj] = useState('');

  const validateCNPJ = (cnpj) => {
    const clean = cnpj.replace(/\D/g, '');
    if (clean.length !== 14) return { isValid: false, message: 'CNPJ deve ter 14 dígitos' };
    
    // Verificar se todos os dígitos são iguais
    if (/^(\d)\1{13}$/.test(clean)) return { isValid: false, message: 'CNPJ inválido' };
    
    // Validar dígitos verificadores
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(clean[i]) * weights1[i];
    }
    let remainder = sum % 11;
    let digit1 = remainder < 2 ? 0 : 11 - remainder;
    
    if (digit1 !== parseInt(clean[12])) return { isValid: false, message: 'CNPJ inválido' };
    
    sum = 0;
    for (let i = 0; i < 13; i++) {
      sum += parseInt(clean[i]) * weights2[i];
    }
    remainder = sum % 11;
    let digit2 = remainder < 2 ? 0 : 11 - remainder;
    
    if (digit2 !== parseInt(clean[13])) return { isValid: false, message: 'CNPJ inválido' };
    
    return { isValid: true, message: 'CNPJ válido' };
  };

  const formatCNPJ = (value) => {
    const clean = value.replace(/\D/g, '');
    return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const cnpjValidation = validateCNPJ(cnpj);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-md mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            CNPJ
          </label>
          <input
            type="text"
            value={cnpj}
            onChange={(e) => setCnpj(formatCNPJ(e.target.value))}
            placeholder="00.000.000/0000-00"
            maxLength={18}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
          />
        </div>

        {cnpj && (
          <div className={`text-center p-4 rounded-lg ${
            cnpjValidation.isValid 
              ? 'bg-green-50 text-green-700' 
              : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-center justify-center space-x-2">
              {cnpjValidation.isValid ? (
                <Check className="text-green-500" size={20} />
              ) : (
                <X className="text-red-500" size={20} />
              )}
              <span className="font-medium">{cnpjValidation.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador de Hash Seguro
const HashGenerator = () => {
  const [text, setText] = useState('');
  const [hashResult, setHashResult] = useState('');

  const generateHash = async () => {
    if (!text.trim()) {
      toast.error('Digite um texto para gerar o hash');
      return;
    }

    try {
      const data = await apiPost('/api/security/secure-hash', { text });
      setHashResult(data);
    } catch (error) {
      toast.error('Erro ao gerar hash');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Texto para Hash
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Digite o texto que deseja converter em hash..."
            className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100 resize-none"
          />
        </div>

        <button
          onClick={generateHash}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Gerar Hash
        </button>

        {hashResult && (
          <div className="space-y-4">
            {Object.entries(hashResult).map(([algorithm, hash]) => (
              <div key={algorithm} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {algorithm}
                  </span>
                  <button
                    onClick={() => copyToClipboard(hash)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                  >
                    <Copy size={16} />
                  </button>
                </div>
                <code className="text-xs font-mono text-gray-900 break-all">
                  {hash}
                </code>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Verificador de Hash
const HashVerifier = () => {
  const [text, setText] = useState('');
  const [hash, setHash] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);

  const verifyHash = async () => {
    if (!text.trim() || !hash.trim()) {
      toast.error('Digite o texto e o hash para verificar');
      return;
    }

    try {
      const data = await apiPost('/api/security/hash-verify', { text, hash });
      setVerificationResult(data);
    } catch (error) {
      toast.error('Erro ao verificar hash');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Texto Original
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Digite o texto original..."
            className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Hash para Verificar
          </label>
          <input
            type="text"
            value={hash}
            onChange={(e) => setHash(e.target.value)}
            placeholder="Cole o hash aqui..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
          />
        </div>

        <button
          onClick={verifyHash}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Verificar Hash
        </button>

        {verificationResult && (
          <div className={`text-center p-4 rounded-lg ${
            verificationResult.isValid 
              ? 'bg-green-50 text-green-700' 
              : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-center justify-center space-x-2">
              {verificationResult.isValid ? (
                <Check className="text-green-500" size={20} />
              ) : (
                <X className="text-red-500" size={20} />
              )}
              <span className="font-medium">{verificationResult.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Security; 